import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { financialReportKeys } from '@/lib/query-keys';
import { financialService } from '@/services/financial.service';
import { useFinancialOverviewStore } from '@/stores/financialOverviewStore';
import { fyLabel, fyMonths, fyOf, fyOfMonth, monthLong, monthShort, monthShortYear, summarise } from '@/lib/financialOverview';
import type { OverviewMonth } from '@/types/table';

const NO_MONTHS: OverviewMonth[] = [];

/**
 * The Financial Overview's data and period selection. The API returns every
 * month, so changing the year or month is instant and needs no new request.
 */
export function useFinancialOverview() {
  const query = useQuery({
    queryKey: financialReportKeys.overview(),
    queryFn: () => financialService.getOverview(),
  });
  const { fy, month, setFy, setMonth } = useFinancialOverviewStore();
  const data = query.data?.data;
  const months = data?.months ?? NO_MONTHS;

  return useMemo(() => {
    const byMonth = new Map(months.map((m) => [m.month, m]));

    // Every year with entries, plus the current one even before it has any.
    const years = [...new Set([fyOf(new Date()), ...months.map((m) => fyOfMonth(m.month))])].sort().reverse();
    const yearOptions = [...years.map((y) => ({ value: y, label: fyLabel(y) })), { value: 'all', label: 'All time' }];

    // The slots along the month picker and chart: April–March for a year,
    // or just the months that exist for all time.
    const slots = fy === 'all' ? months.map((m) => m.month) : fyMonths(fy);
    const inYear = slots.flatMap((slot) => byMonth.get(slot) ?? []);
    const selected = month ? inYear.filter((m) => m.month === month) : inYear;
    const summary = summarise(selected);

    const first = selected[0]?.month;
    const last = selected[selected.length - 1]?.month;
    const yearName = fy === 'all' ? 'All time' : `FY ${fy}`;
    const span = !first ? '' : first === last ? monthShortYear(first) : `${monthShortYear(first)} – ${monthShortYear(last)}`;
    const toDate = !month && fy === fyOf(new Date()) && selected.length > 0 ? ' (to date)' : '';

    return {
      isLoading: query.isLoading,
      isError: query.isError,
      currency: data?.currency ?? 'INR',
      ledgerThrough: data?.ledgerThrough ?? null,
      unpaidInvoices: data?.unpaidInvoices ?? [],
      unbilledQuotes: data?.unbilledQuotes ?? [],
      fy, month, setFy, setMonth, yearOptions,
      slots: slots.map((slot) => ({ month: slot, label: monthShort(slot), data: byMonth.get(slot) ?? null })),
      inYear,
      summary,
      lastMonth: last ?? null,
      periodLabel: month ? `${monthLong(month)} · ${yearName}` : span ? `${yearName} · ${span}${toDate}` : yearName,
      periodShort: month ? monthLong(month) : span,
    };
  }, [months, data, fy, month, setFy, setMonth, query.isLoading, query.isError]);
}
